/**
 * Catalog Store
 * Manages catalog data with session-level caching to minimize Firestore reads.
 *
 * ── Catalog Modes & Route Flow ──────────────────────────────────
 *
 * The catalog supports four modes based on authentication and context:
 *
 * 1. VISITOR MODE (catalogMode = 'visitor')
 *    Route:  /app/catalog  (meta: { allowGuest: true })
 *    Auth:   Not authenticated
 *    Data:   Assortment 10001 items ONLY
 *    Skips:  Prices, Promotions (Sales), Amount Discounts, Cart
 *    UI:     showPrices=false, no promo department, no cart button
 *    Rules:  Firestore rules must allow unauthenticated reads on:
 *            - Catalog/{makat}        (allow read: if true)
 *            - Assortments/{id}       (allow read: if true)
 *            - Departments + Subs     (already allow read: if true)
 *
 * 2. DEFAULT MODE (catalogMode = 'default')
 *    Route:  /app/catalog
 *    Auth:   Authenticated, no linked client (or client doc missing)
 *    Data:   Default assortment + default prices + promotions + discounts
 *
 * 3. CLIENT MODE (catalogMode = 'client')
 *    Route:  /app/catalog
 *    Auth:   Authenticated, with linked client (client_makat in profile)
 *    Data:   Client's assortment + client's price list + promotions + discounts
 *
 * 4. AGENT MODE (catalogMode = 'agent')
 *    Route:  /app/agents/catalog/:clientMakat
 *    Auth:   Authenticated agent, viewing a specific client's catalog
 *    Data:   Override client's assortment + prices + promotions + discounts
 *
 * ── Auth Transition Handling ────────────────────────────────────
 * When a visitor logs in (or an authenticated user logs out), the
 * bootstrap() function detects the mismatch between catalogMode and
 * auth state, resets the store, and re-bootstraps with the correct mode.
 * This ensures seamless transitions without stale data.
 * ────────────────────────────────────────────────────────────────
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDocument } from '../../core/firebase/firestore.js'
import { getFirebaseDb } from '../../core/firebase/firebase.js'
import { collection, query, orderBy, getDocs, where, documentId } from 'firebase/firestore'
import logger from '../../core/services/logger.js'
import { useAuthStore } from './auth.js'
import { usePromotionsStore } from './promotions.js'
import { useAmountDiscountsStore } from './amountDiscounts.js'
import { useCartStore } from './cart.js'
import { getPromoMakats } from '../../core/utils/promoCalc.js'

const log = logger.createScoped('CatalogStore')

// ============================================
// Constants
// ============================================
const DEFAULT_ASSORTMENT_ID = '10001'
const DEFAULT_PRICE_LIST_ID = 'בסיס'
const FIRESTORE_IN_QUERY_LIMIT = 30

// View preferences constants
const VIEW_STORAGE_KEY = 'gofood:catalog:itemsPerRow'
const DISPLAY_MODE_STORAGE_KEY = 'gofood:catalog:displayMode'
const AUTO_MODE_STORAGE_KEY = 'gofood:catalog:autoMode'
const DESKTOP_VIEW_OPTIONS = [2, 3, 4, 5, 6]
const TABLET_VIEW_OPTIONS = [1, 2, 3]
const MOBILE_VIEW_OPTIONS = [1, 2]
const DEFAULT_DESKTOP_ITEMS = 3
const DEFAULT_MOBILE_ITEMS = 1
const DEFAULT_DISPLAY_MODE = 'grid'

// Virtual "מבצעים" department constant
const PROMO_DEPARTMENT_ID = '__promotions__'
const PROMO_DEPARTMENT = {
  id: PROMO_DEPARTMENT_ID,
  name: 'מבצעים',
  order: -Infinity,
  isVirtual: true,
  isPromoDept: true,
}

// Popup message for missing client document (exact text from requirements)
const MISSING_CLIENT_MESSAGE =
  'לא נמצא כרטיס לקוח, הקטלוג נטען על בסיס נתונים כלליים. אנא פנה למחלקת מכירות להקמת כרטיס לקוח.'

function loadItemsPerRowFromStorage() {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY)
    if (stored) {
      const val = parseInt(stored, 10)
      if (!isNaN(val)) return val
    }
  } catch (_e) {}
  return DEFAULT_DESKTOP_ITEMS
}

function loadDisplayModeFromStorage() {
  try {
    const stored = localStorage.getItem(DISPLAY_MODE_STORAGE_KEY)
    if (stored === 'grid' || stored === 'cards') return stored
  } catch (_e) {}
  return DEFAULT_DISPLAY_MODE
}

function loadAutoModeFromStorage() {
  try {
    const stored = localStorage.getItem(AUTO_MODE_STORAGE_KEY)
    if (stored === 'false') return false
  } catch (_e) {}
  return true
}

/**
 * Split array into chunks of specified size
 */
function chunkArray(array, size) {
  const chunks = []
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size))
  }
  return chunks
}

export const useCatalogStore = defineStore('catalog', () => {
  // ============================================
  // Bootstrap State
  // ============================================
  const isBootstrapped = ref(false)
  const isBootstrapping = ref(false)
  const bootstrapError = ref(null)
  const catalogLoading = ref(false) // Separate loading state for catalog items

  // ============================================
  // Catalog Data (loaded once per session)
  // ============================================
  const itemsByMakat = ref(new Map())
  const pricesByMakat = ref(new Map())
  const assortmentMakats = ref([])
  const assortmentId = ref(null)
  const priceListId = ref(null)
  const catalogMode = ref('default') // 'default' | 'client'
  const linkedClient = ref(null) // Client object when in client mode
  const clientMissingMessage = ref(null) // Popup message when client doc missing

  // ============================================
  // Departments Data
  // ============================================
  const departments = ref([])
  const departmentsLoading = ref(false)
  const departmentsError = ref(null)
  const subsByDepartmentId = ref(new Map())
  const allSubsLoaded = ref(false)
  const subsLoading = ref(false)

  // ============================================
  // Selection State
  // ============================================
  const selectedDepartmentId = ref(null)
  const selectedDepartmentName = ref(null)
  const selectedSubDepartmentId = ref(null)
  const selectedSubDepartmentName = ref(null)

  // ============================================
  // Search State
  // ============================================
  const searchQuery = ref('')
  const searchTouched = ref(false)

  // ============================================
  // Filter State
  // ============================================
  const filterShowOutOfStock = ref(false) // Default: hide out-of-stock items
  const filterKosher = ref(false) // Default: don't filter by kosher
  const filterRabanut = ref(false) // Default: don't filter by rabanut
  const filterShowNotInAssortment = ref(false)
  const notInAssortmentItemsByMakat = ref(new Map())
  const notInAssortmentMakats = ref([])
  const notInAssortmentLoading = ref(false)
  const notInAssortmentLoaded = ref(false)

  // ============================================
  // View Preferences State
  // ============================================
  const itemsPerRow = ref(loadItemsPerRowFromStorage())
  const displayMode = ref(loadDisplayModeFromStorage())
  const isAutoMode = ref(loadAutoModeFromStorage())
  const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024)

  const isMobileView = computed(() => windowWidth.value < 500)
  const isTabletView = computed(() => windowWidth.value >= 500 && windowWidth.value < 1024)
  const isPCView = computed(() => windowWidth.value >= 1024)
  const currentViewOptions = computed(() => {
    if (isPCView.value) return DESKTOP_VIEW_OPTIONS
    if (isTabletView.value) return TABLET_VIEW_OPTIONS
    return MOBILE_VIEW_OPTIONS
  })

  const autoItemsPerRow = computed(() => {
    const w = windowWidth.value
    if (w < 500) return 2
    if (w < 1100) return 3
    if (w < 1300) return 4
    return 5
  })

  const validItemsPerRow = computed(() => {
    if (isAutoMode.value) return autoItemsPerRow.value
    const options = currentViewOptions.value
    if (options.includes(itemsPerRow.value)) return itemsPerRow.value
    return autoItemsPerRow.value
  })

  const isGridMode = computed(() => displayMode.value === 'grid')

  /**
   * True when catalog is in visitor mode (unauthenticated user).
   * Visitors see items only — no prices, promotions, or discounts.
   */
  const isVisitorMode = computed(() => catalogMode.value === 'visitor')

  /**
   * Whether the "show not in assortment" filter toggle should be visible.
   * Only for client/agent modes when assortment is NOT the master (10001).
   */
  const canShowNotInAssortmentFilter = computed(() => {
    const mode = catalogMode.value
    if (mode !== 'client' && mode !== 'agent') return false
    return assortmentId.value !== DEFAULT_ASSORTMENT_ID
  })

  const gridStyle = computed(() => ({
    gridTemplateColumns: `repeat(${validItemsPerRow.value}, 1fr)`,
    gap: isGridMode.value ? '0' : undefined,
  }))

  // ============================================
  // Getters / Computed
  // ============================================

  /**
   * All catalog items as array (preserving assortment order)
   */
  const itemsArray = computed(() => {
    return assortmentMakats.value
      .map((makat) => itemsByMakat.value.get(makat))
      .filter(Boolean)
      .filter((item) => item.status !== 'אסור למכירה')
  })

  /**
   * All displayable items — regular assortment + not-in-assortment extras when filter is ON
   */
  const allDisplayableItems = computed(() => {
    if (!filterShowNotInAssortment.value) return itemsArray.value
    const extra = [...notInAssortmentItemsByMakat.value.values()]
    return extra.length ? [...itemsArray.value, ...extra] : itemsArray.value
  })

  // ============================================
  // Department/Sub-department Visibility
  // ============================================

  /**
   * Items used to determine department/sub-department visibility.
   * Uses assortment items only — unless "show not in assortment" filter is ON,
   * in which case not-in-assortment items are included so their departments/sub-departments
   * become visible again (they were stripped earlier because the client had no items there).
   * When filterShowOutOfStock is OFF, also excludes out-of-stock items.
   */
  const visibleItemsForDeptCheck = computed(() => {
    const base = filterShowNotInAssortment.value ? allDisplayableItems.value : itemsArray.value
    if (filterShowOutOfStock.value) return base
    return base.filter((item) => item.showOnWeb !== 'H')
  })

  /**
   * Set of department IDs that have at least one visible item
   */
  const deptIdsWithVisibleItems = computed(() => {
    const set = new Set()
    for (const item of visibleItemsForDeptCheck.value) {
      if (item.department) set.add(item.department)
    }
    return set
  })

  /**
   * Map<deptId, Set<subDeptId>> — for each dept, which sub-depts have visible items
   */
  const subDeptIdsWithVisibleItemsByDept = computed(() => {
    const map = new Map()
    for (const item of visibleItemsForDeptCheck.value) {
      if (item.department && item.subDepartment) {
        if (!map.has(item.department)) map.set(item.department, new Set())
        map.get(item.department).add(item.subDepartment)
      }
    }
    return map
  })

  /**
   * Departments array with virtual "מבצעים" department prepended if promotions exist
   */
  const departmentsWithPromo = computed(() => {
    const promotionsStore = usePromotionsStore()
    if (promotionsStore.count > 0) {
      return [PROMO_DEPARTMENT, ...departments.value]
    }
    return departments.value
  })

  /**
   * Departments filtered to only those with visible items.
   * __promotions__ department is always shown (exempt from hiding).
   */
  const visibleDepartments = computed(() => {
    return departmentsWithPromo.value.filter((dept) => {
      if (dept.id === PROMO_DEPARTMENT_ID) return true
      return deptIdsWithVisibleItems.value.has(dept.id)
    })
  })

  /**
   * Current department's sub-departments filtered to only those with visible items
   */
  const visibleCurrentDeptSubs = computed(() => {
    if (!selectedDepartmentId.value) return []
    const subs = currentDeptSubs.value
    const visibleSubIds = subDeptIdsWithVisibleItemsByDept.value.get(selectedDepartmentId.value)
    if (!visibleSubIds || visibleSubIds.size === 0) return []
    return subs.filter((sub) => visibleSubIds.has(sub.id))
  })

  /**
   * Get visible sub-departments for any department (used by swipe nav & search)
   */
  function getVisibleSubsForDept(deptId) {
    const allSubs = subsByDepartmentId.value.get(deptId) || []
    const visibleSubIds = subDeptIdsWithVisibleItemsByDept.value.get(deptId)
    if (!visibleSubIds || visibleSubIds.size === 0) return []
    return allSubs.filter((sub) => visibleSubIds.has(sub.id))
  }

  /**
   * Filtered subsByDeptIdObject for search — only includes subs with visible items
   */
  const visibleSubsByDeptIdObject = computed(() => {
    const obj = {}
    for (const [deptId, subs] of subsByDepartmentId.value) {
      const visibleSubIds = subDeptIdsWithVisibleItemsByDept.value.get(deptId)
      if (visibleSubIds && visibleSubIds.size > 0) {
        obj[deptId] = subs.filter((sub) => visibleSubIds.has(sub.id))
      }
    }
    return obj
  })

  /**
   * Items that belong to any promotion, sorted by first_order priority
   * first_order items come first, then regular promo items
   */
  const promoItems = computed(() => {
    const promotionsStore = usePromotionsStore()
    if (promotionsStore.count === 0) return []

    // Filter items that have at least one ELIGIBLE promotion
    // This excludes first_order promos for non-eligible users
    const itemsWithPromos = itemsArray.value.filter((item) => {
      const promos = promotionsStore.getEligiblePromosForMakat(item.makat)
      return promos && promos.length > 0
    })

    // Sort: first_order promos first (tier 0), then regular promos (tier 1)
    return itemsWithPromos.sort((a, b) => {
      const promosA = promotionsStore.getEligiblePromosForMakat(a.makat)
      const promosB = promotionsStore.getEligiblePromosForMakat(b.makat)

      const hasFirstOrderA = promosA.some((p) => p.first_order === true)
      const hasFirstOrderB = promosB.some((p) => p.first_order === true)

      const tierA = hasFirstOrderA ? 0 : 1
      const tierB = hasFirstOrderB ? 0 : 1

      // Sort by tier, then by name as fallback for deterministic order
      if (tierA !== tierB) return tierA - tierB
      return (a.name || '').localeCompare(b.name || '', 'he')
    })
  })

  /**
   * Items from single-item promotions only
   * Multi-item promotions are rendered as PromotionCard instead
   */
  const singlePromoItems = computed(() => {
    const promotionsStore = usePromotionsStore()
    if (promotionsStore.count === 0) return []

    const singleMakats = promotionsStore.getSinglePromoMakats()

    return itemsArray.value
      .filter((item) => singleMakats.includes(item.makat))
      .sort((a, b) => {
        const promosA = promotionsStore.getEligiblePromosForMakat(a.makat)
        const promosB = promotionsStore.getEligiblePromosForMakat(b.makat)
        const hasFirstOrderA = promosA.some((p) => p.first_order)
        const hasFirstOrderB = promosB.some((p) => p.first_order)
        if (hasFirstOrderA !== hasFirstOrderB) return hasFirstOrderA ? -1 : 1
        return (a.name || '').localeCompare(b.name || '', 'he')
      })
  })

  /**
   * Multi-item promotions for PromotionCard display
   * Sorted by first_order first, then by end_date
   */
  const multiItemPromotions = computed(() => {
    const promotionsStore = usePromotionsStore()
    const promos = promotionsStore.getMultiItemPromotions()
    // Filter out promotions with no available items in the client's catalog
    const withAvailableItems = promos.filter((promo) => {
      const makats = getPromoMakats(promo)
      const availableCount = makats
        .map((makat) => itemsByMakat.value.get(makat))
        .filter(Boolean)
        .filter((item) => item.status !== 'אסור למכירה').length
      return availableCount > 0
    })
    // Sort: first_order first, then by promo priority
    return [...withAvailableItems].sort((a, b) => {
      if (a.first_order !== b.first_order) return a.first_order ? -1 : 1
      return 0 // Already sorted by priority in getMultiItemPromotions
    })
  })

  /**
   * First_order multi-item promotions (rendered first in promo grid)
   */
  const firstOrderMultiPromos = computed(() => {
    return multiItemPromotions.value.filter((p) => p.first_order === true)
  })

  /**
   * Regular (non-first_order) multi-item promotions
   */
  const regularMultiPromos = computed(() => {
    return multiItemPromotions.value.filter((p) => p.first_order !== true)
  })

  /**
   * First_order single-item promo items (rendered first in promo grid)
   */
  const firstOrderSinglePromoItems = computed(() => {
    const promotionsStore = usePromotionsStore()
    return singlePromoItems.value.filter((item) => {
      const promos = promotionsStore.getEligiblePromosForMakat(item.makat)
      return promos.some((p) => p.first_order === true)
    })
  })

  /**
   * Regular (non-first_order) single-item promo items
   */
  const regularSinglePromoItems = computed(() => {
    const promotionsStore = usePromotionsStore()
    return singlePromoItems.value.filter((item) => {
      const promos = promotionsStore.getEligiblePromosForMakat(item.makat)
      return !promos.some((p) => p.first_order === true)
    })
  })

  /**
   * Currently selected department object
   */
  const selectedDepartment = computed(() => {
    if (!selectedDepartmentId.value) return null
    return departments.value.find((d) => d.id === selectedDepartmentId.value)
  })

  /**
   * Subdepartments for selected department
   */
  const currentDeptSubs = computed(() => {
    if (!selectedDepartmentId.value) return []
    return subsByDepartmentId.value.get(selectedDepartmentId.value) || []
  })

  /**
   * Subdepartments as plain object for components (reactive)
   */
  const subsByDeptIdObject = computed(() => {
    const obj = {}
    for (const [key, value] of subsByDepartmentId.value) {
      obj[key] = value
    }
    return obj
  })

  /**
   * Apply catalog filters (out-of-stock, kosher, rabanut) to an items array
   */
  function applyItemFilters(items) {
    let result = items

    // Filter out-of-stock items (showOnWeb === 'H') when toggle is off
    if (!filterShowOutOfStock.value) {
      result = result.filter((item) => item.showOnWeb !== 'H')
    }

    // Filter by kosher supervision (must have kosher_type value, excluding non-kosher values)
    if (filterKosher.value) {
      const NON_KOSHER_VALUES = ['ללא כשרות', 'לא כשר']
      result = result.filter((item) => {
        const val = (item.kosher_type || '').trim()
        return val !== '' && !NON_KOSHER_VALUES.includes(val)
      })
    }

    // Filter by rabanut approval (only when kosher filter is active)
    if (filterKosher.value && filterRabanut.value) {
      result = result.filter((item) => item.kosher_rabanut === '1' || item.kosher_rabanut === '2')
    }

    return result
  }

  /**
   * Count of active filters (for badge display)
   */
  const activeFilterCount = computed(() => {
    let count = 0
    if (filterShowOutOfStock.value) count++
    if (filterKosher.value) count++
    if (filterKosher.value && filterRabanut.value) count++
    if (filterShowNotInAssortment.value) count++
    return count
  })

  /**
   * Is search active (3+ chars)
   */
  const isSearchActive = computed(() => searchQuery.value.trim().length >= 3)

  /**
   * Search results (in-memory filtering)
   */
  const searchResults = computed(() => {
    if (!isSearchActive.value) return []
    const q = searchQuery.value.trim().toLowerCase()
    const matched = allDisplayableItems.value.filter((item) => {
      const name = (item.name || '').toLowerCase()
      const makat = (item.makat || '').toLowerCase()
      const barcode = (item.barcode || '').toLowerCase()
      return name.includes(q) || makat.includes(q) || barcode.includes(q)
    })
    return applyItemFilters(matched)
  })

  /**
   * Filtered items based on department/subdepartment selection
   */
  const filteredItems = computed(() => {
    if (!selectedDepartmentId.value) return []

    // Handle virtual promo department
    if (selectedDepartmentId.value === PROMO_DEPARTMENT_ID) {
      return applyItemFilters(promoItems.value)
    }

    let items = allDisplayableItems.value.filter(
      (item) => item.department === selectedDepartmentId.value
    )

    if (selectedSubDepartmentId.value) {
      items = items.filter((item) => item.subDepartment === selectedSubDepartmentId.value)
      // Sort by order field within sub-department, not-in-assortment items last
      items = [...items].sort((a, b) => {
        const niaA = a._notInAssortment ? 1 : 0
        const niaB = b._notInAssortment ? 1 : 0
        if (niaA !== niaB) return niaA - niaB
        const hasOrderA = typeof a.order === 'number'
        const hasOrderB = typeof b.order === 'number'
        if (hasOrderA && hasOrderB) return a.order - b.order
        if (hasOrderA && !hasOrderB) return -1
        if (!hasOrderA && hasOrderB) return 1
        return 0
      })
    } else {
      // When no subdepartment selected, sort by subdepartment order, then by item order within each sub
      const subOrder = {}
      currentDeptSubs.value.forEach((sub, index) => {
        subOrder[sub.id] = index
      })
      items = [...items].sort((a, b) => {
        // Not-in-assortment items always sort last
        const niaA = a._notInAssortment ? 1 : 0
        const niaB = b._notInAssortment ? 1 : 0
        if (niaA !== niaB) return niaA - niaB
        const subOrderA = subOrder[a.subDepartment] ?? 999
        const subOrderB = subOrder[b.subDepartment] ?? 999
        if (subOrderA !== subOrderB) return subOrderA - subOrderB
        const hasOrderA = typeof a.order === 'number'
        const hasOrderB = typeof b.order === 'number'
        if (hasOrderA && hasOrderB) return a.order - b.order
        if (hasOrderA && !hasOrderB) return -1
        if (!hasOrderA && hasOrderB) return 1
        return 0
      })
    }

    return applyItemFilters(items)
  })

  /**
   * Visible items based on search or selection
   */
  const visibleItems = computed(() => {
    if (isSearchActive.value) return searchResults.value
    return filteredItems.value
  })

  /**
   * Show search helper text (1-2 chars typed)
   */
  const showSearchHelper = computed(() => {
    return (
      searchTouched.value &&
      searchQuery.value.trim().length > 0 &&
      searchQuery.value.trim().length < 3
    )
  })

  // ============================================
  // Internal: Fetch Catalog Items by Makats
  // ============================================
  async function fetchCatalogItemsByMakats(makats) {
    if (!makats || makats.length === 0) {
      return new Map()
    }

    const db = getFirebaseDb()
    const catalogRef = collection(db, 'Catalog')
    const chunks = chunkArray(makats, FIRESTORE_IN_QUERY_LIMIT)
    const results = new Map()

    log.info(`Fetching ${makats.length} catalog items in ${chunks.length} chunks`)

    const chunkPromises = chunks.map((chunk) => {
      const q = query(catalogRef, where(documentId(), 'in', chunk))
      return getDocs(q)
    })

    const snapshots = await Promise.all(chunkPromises)

    for (const snapshot of snapshots) {
      snapshot.forEach((docSnap) => {
        results.set(docSnap.id, { id: docSnap.id, makat: docSnap.id, ...docSnap.data() })
      })
    }

    log.info(`Fetched ${results.size} catalog items`)
    return results
  }

  // ============================================
  // Actions
  // ============================================

  // ============================================
  // Agent Override State
  // ============================================
  const agentOverrideClient = ref(null) // When agent views a client's catalog

  /**
   * Bootstrap the catalog store
   * Loads all data in parallel for fastest load.
   *
   * ── Catalog Modes ─────────────────────────────────────────────
   * | Mode      | Auth  | Data loaded                            |
   * |-----------|-------|----------------------------------------|
   * | 'visitor' | No    | Assortment 10001 + Catalog items ONLY  |
   * |           |       | No prices, promotions, or discounts    |
   * | 'default' | Yes   | Default assortment + prices            |
   * |           |       | + promotions + discounts                |
   * | 'client'  | Yes   | Client-specific assortment + prices    |
   * |           |       | + promotions + discounts                |
   * | 'agent'   | Yes   | Agent override: any client's catalog   |
   * ─────────────────────────────────────────────────────────────
   *
   * Re-bootstrap triggers automatically when auth state changes:
   * - visitor → authenticated: reloads with prices/promos/discounts
   * - authenticated → visitor: strips prices/promos/discounts
   * - agent override: reloads with different client's data
   *
   * @param {Object} userProfile - User profile (optional, used for legacy calls)
   * @param {Object} options - Bootstrap options
   * @param {Object} options.overrideClient - Client object to use instead of auth user's client (agent mode)
   */
  async function bootstrap(userProfile, options = {}) {
    const isAgentOverride = !!options.overrideClient
    const authStore = useAuthStore()

    // ── Re-bootstrap detection ──────────────────────────────────
    // When auth state changes after a previous bootstrap, we must
    // reload to match the new context (visitor↔authenticated).
    const authStateChanged =
      isBootstrapped.value &&
      ((catalogMode.value === 'visitor' && authStore.isAuthenticated) ||
        (!authStore.isAuthenticated && catalogMode.value !== 'visitor'))

    if (isBootstrapped.value && !isAgentOverride && !authStateChanged) {
      log.debug('Already bootstrapped, skipping')
      return
    }

    // Agent overrides and auth transitions must always proceed —
    // a stale in-progress bootstrap must not block them.
    if (isBootstrapping.value && !isAgentOverride && !authStateChanged) {
      log.debug('Bootstrap in progress, skipping')
      return
    }

    // Reset state if switching context (agent override or auth transition)
    if (isAgentOverride || authStateChanged) {
      if (authStateChanged) {
        log.info('Auth state changed, re-bootstrapping catalog', {
          previousMode: catalogMode.value,
          isAuthenticated: authStore.isAuthenticated,
        })
      }
      // Preserve not-in-assortment data when re-bootstrapping for the same client
      const sameClient =
        isAgentOverride &&
        options.overrideClient?.makat &&
        linkedClient.value?.makat === options.overrideClient.makat
      const savedNia = sameClient
        ? {
            filter: filterShowNotInAssortment.value,
            items: notInAssortmentItemsByMakat.value,
            makats: notInAssortmentMakats.value,
            loaded: notInAssortmentLoaded.value,
          }
        : null

      reset()

      if (savedNia) {
        filterShowNotInAssortment.value = savedNia.filter
        notInAssortmentItemsByMakat.value = savedNia.items
        notInAssortmentMakats.value = savedNia.makats
        notInAssortmentLoaded.value = savedNia.loaded
      }
    }

    isBootstrapping.value = true
    catalogLoading.value = true
    bootstrapError.value = null
    agentOverrideClient.value = options.overrideClient || null

    try {
      // Load catalog and departments in parallel
      await Promise.all([loadCatalogData(userProfile, options), loadDepartments()])

      // Auto-select department: promo dept if promotions exist, else first real dept
      const promotionsStore = usePromotionsStore()
      if (promotionsStore.count > 0) {
        // Select virtual promo department - no subs to load
        selectedDepartmentId.value = PROMO_DEPARTMENT_ID
        selectedDepartmentName.value = PROMO_DEPARTMENT.name
        selectedSubDepartmentId.value = null
        selectedSubDepartmentName.value = null
        log.info('Auto-selected promo department', { promoCount: promotionsStore.count })
      } else if (departments.value.length > 0) {
        const firstDept = departments.value[0]
        selectedDepartmentId.value = firstDept.id
        selectedDepartmentName.value = firstDept.name
        await loadSubsForDepartment(firstDept.id)
      }

      isBootstrapped.value = true
      log.info('Catalog bootstrap complete', {
        itemsCount: itemsByMakat.value.size,
        departmentsCount: departments.value.length,
      })

      // Restore cart from Firestore after catalog is ready
      // Only for authenticated, non-agent, non-visitor modes
      const authStoreRef = useAuthStore()
      if (!isAgentOverride && authStoreRef.isAuthenticated) {
        const cartStore = useCartStore()
        await cartStore.restoreFromFirestore()
      }
    } catch (err) {
      log.error('Catalog bootstrap failed', err)
      bootstrapError.value = err.message || 'Failed to load catalog'
    } finally {
      isBootstrapping.value = false
      catalogLoading.value = false
    }
  }

  /**
   * Load catalog data (assortment, prices, items)
   * Handles both default and client-linked modes
   * @param {Object} userProfile - User profile (optional)
   * @param {Object} options - Options including overrideClient for agent mode
   */
  async function loadCatalogData(userProfile, options = {}) {
    const authStore = useAuthStore()

    // Clear any previous popup message
    clientMissingMessage.value = null
    linkedClient.value = null
    usePromotionsStore().setLinkedClient(null)

    let useAssortmentId = DEFAULT_ASSORTMENT_ID
    let usePriceListId = DEFAULT_PRICE_LIST_ID
    let isClientMode = false
    let client = null
    let clientMissing = false

    // Check if we have an override client (agent mode)
    if (options.overrideClient) {
      client = options.overrideClient
      isClientMode = true
      catalogMode.value = 'agent' // New mode for agent viewing client
      linkedClient.value = client
      usePromotionsStore().setLinkedClient(client)

      // Use override client's merakez and price_list_id
      if (client.merakez) {
        useAssortmentId = client.merakez
      } else {
        log.warn('Override client missing merakez, using default assortment', {
          clientMakat: client.makat,
        })
      }

      if (client.price_list_id) {
        usePriceListId = client.price_list_id
      } else {
        log.warn('Override client missing price_list_id, using default price list', {
          clientMakat: client.makat,
        })
      }

      log.info('Loading agent-override catalog', {
        clientMakat: client.makat,
        clientName: client.name,
        assortmentId: useAssortmentId,
        priceListId: usePriceListId,
      })
    } else if (!authStore.isAuthenticated) {
      // ── Visitor Mode ──────────────────────────────────────────────
      // Unauthenticated user browsing the catalog.
      // • Assortment: DEFAULT_ASSORTMENT_ID ('10001') — items only
      // • Prices:     SKIPPED (Firestore rules block unauthenticated reads)
      // • Promotions: SKIPPED (no auth → no Sales collection access)
      // • Discounts:  SKIPPED (no auth → no AmountDiscounts access)
      // • Cart:       SKIPPED (no user to persist cart for)
      // Items are loaded with price: null and showPrices is false in the UI.
      // ─────────────────────────────────────────────────────────────
      catalogMode.value = 'visitor'
      log.info('Loading visitor catalog (no auth) — items only, no prices/promos/discounts', {
        assortmentId: useAssortmentId,
      })
    } else {
      // Normal flow: determine catalog mode based on role and client link
      const result = await authStore.loadUserClientIfAvailable()
      client = result.client
      clientMissing = result.clientMissing

      if (clientMissing) {
        // Client makat exists but client doc not found - fallback to defaults and show popup
        log.warn('Client document missing, falling back to defaults')
        clientMissingMessage.value = MISSING_CLIENT_MESSAGE
        catalogMode.value = 'default'
      } else if (client) {
        // Client loaded successfully - use client's assortment and price list
        isClientMode = true
        catalogMode.value = 'client'
        linkedClient.value = client
        usePromotionsStore().setLinkedClient(client)

        // Use client's merakez for assortment, price_list_id for prices
        if (client.merakez) {
          useAssortmentId = client.merakez
        } else {
          log.warn('Client missing merakez, using default assortment', {
            clientMakat: client.makat,
          })
        }

        if (client.price_list_id) {
          usePriceListId = client.price_list_id
        } else {
          log.warn('Client missing price_list_id, using default price list', {
            clientMakat: client.makat,
          })
        }

        log.info('Loading client-linked catalog', {
          clientMakat: client.makat,
          assortmentId: useAssortmentId,
          priceListId: usePriceListId,
        })
      } else {
        // No client link - use defaults
        catalogMode.value = 'default'
        log.info('Loading default catalog', {
          assortmentId: useAssortmentId,
          priceListId: usePriceListId,
        })
      }
    }

    assortmentId.value = useAssortmentId
    priceListId.value = usePriceListId

    const isVisitor = catalogMode.value === 'visitor'

    // ── Fire all independent fetches in parallel ─────────────────
    // Dependency graph:
    //   promotions  ──┐
    //   discounts   ──┤ (all independent, need only assortmentId/priceListId)
    //   assortment  ──┤──→ catalog items (needs assortmentMakats)
    //   prices      ──┘──→ merge (needs priceMap + fetchedItems)

    // 1) Promotions & Discounts (authenticated only, non-blocking)
    let promotionsPromise = Promise.resolve()
    if (!isVisitor) {
      const promotionsStore = usePromotionsStore()
      promotionsPromise = promotionsStore.loadPromotionsForMerakez(useAssortmentId).catch((err) => {
        log.warn('Promotions load failed (non-blocking)', { error: err })
      })

      // Amount discounts (fire-and-forget)
      const amountDiscountsStore = useAmountDiscountsStore()
      const discountOptions = options.overrideClient
        ? { assortmentIdOverride: useAssortmentId }
        : {}
      amountDiscountsStore.loadForCurrentUser(discountOptions).catch((err) => {
        log.warn('Amount discounts load failed (non-blocking)', { error: err })
      })
    } else {
      log.info('Skipping promotions & discounts (visitor mode)')
    }

    // 2) Assortment doc (parallel)
    const assortmentPromise = getDocument('Assortments', useAssortmentId)

    // 3) Prices doc (parallel, authenticated only)
    let pricesPromise = Promise.resolve(null)
    if (!isVisitor) {
      pricesPromise = getDocument('Prices', usePriceListId)
    } else {
      log.info('Prices skipped (visitor mode)')
    }

    // ── Await assortment (needed for catalog items fetch) ────────
    let assortment = await assortmentPromise

    // Fallback if client's assortment not found
    if (!assortment && isClientMode && useAssortmentId !== DEFAULT_ASSORTMENT_ID) {
      log.warn('Client assortment not found, falling back to default', {
        assortmentId: useAssortmentId,
      })
      assortment = await getDocument('Assortments', DEFAULT_ASSORTMENT_ID)
      assortmentId.value = DEFAULT_ASSORTMENT_ID
    }

    if (!assortment) {
      throw new Error(`Assortment ${useAssortmentId} not found`)
    }

    assortmentMakats.value = assortment.itemMakats || []

    // Client-specific additions: client_products entries with valid !== 'Y' extend the base assortment
    if (
      isClientMode &&
      Array.isArray(client?.client_products) &&
      client.client_products.length > 0
    ) {
      const existing = new Set(assortmentMakats.value)
      const additions = client.client_products
        .filter((p) => p && p.makat && p.valid !== 'Y' && !existing.has(p.makat))
        .map((p) => p.makat)
      if (additions.length > 0) {
        assortmentMakats.value = [...assortmentMakats.value, ...additions]
        log.info('Client product additions applied', {
          clientMakat: client.makat,
          additionsCount: additions.length,
        })
      }
    }

    log.info('Assortment loaded', {
      assortmentId: assortmentId.value,
      itemsCount: assortmentMakats.value.length,
    })

    if (assortmentMakats.value.length === 0) {
      log.warn('Assortment has no items')
      return
    }

    // ── Fetch catalog items (needs assortmentMakats) ─────────────
    // Prices are still loading in parallel — we don't need them until merge
    const fetchedItems = await fetchCatalogItemsByMakats(assortmentMakats.value)

    // ── Await prices (needed for merge) ──────────────────────────
    const priceMap = new Map()
    if (!isVisitor) {
      let priceList = await pricesPromise

      // Fallback if client's price list not found
      if (!priceList && isClientMode && usePriceListId !== DEFAULT_PRICE_LIST_ID) {
        log.warn('Client price list not found, falling back to default', {
          priceListId: usePriceListId,
        })
        priceList = await getDocument('Prices', DEFAULT_PRICE_LIST_ID)
        priceListId.value = DEFAULT_PRICE_LIST_ID
      }

      if (!priceList) {
        throw new Error(`Price list ${usePriceListId} not found`)
      }

      if (priceList.items && Array.isArray(priceList.items)) {
        for (const item of priceList.items) {
          if (item.makat && item.price !== undefined) {
            priceMap.set(item.makat, item.price)
          }
        }
      }
      log.info('Prices loaded', {
        priceListId: priceListId.value,
        pricesCount: priceMap.size,
      })
    }
    pricesByMakat.value = priceMap

    // ── Await promotions (needed before bootstrap selects promo dept) ──
    await promotionsPromise

    // ── Merge prices into items ──────────────────────────────────
    const itemsMap = new Map()
    for (const [makat, item] of fetchedItems) {
      const price = priceMap.get(makat) ?? null
      itemsMap.set(makat, { ...item, price })
    }
    itemsByMakat.value = itemsMap

    log.info('Catalog loaded successfully', {
      mode: catalogMode.value,
      assortmentId: assortmentId.value,
      priceListId: priceListId.value,
      totalItems: itemsMap.size,
      withPrice: [...itemsMap.values()].filter((i) => i.price !== null).length,
      client: linkedClient.value?.makat || null,
    })
  }

  /**
   * Load items from the master assortment (10001) that are NOT in the current client's assortment.
   * Lazy-loaded on first toggle. Flags each item with _notInAssortment: true.
   */
  async function loadNotInAssortmentItems() {
    if (notInAssortmentLoaded.value || notInAssortmentLoading.value) return
    notInAssortmentLoading.value = true

    try {
      const masterAssortment = await getDocument('Assortments', DEFAULT_ASSORTMENT_ID)
      if (!masterAssortment?.itemMakats) {
        log.warn('Master assortment not found or empty')
        return
      }

      const clientSet = new Set(assortmentMakats.value)
      const diffMakats = masterAssortment.itemMakats.filter((m) => !clientSet.has(m))

      if (diffMakats.length === 0) {
        log.info('No items outside client assortment')
        notInAssortmentLoaded.value = true
        return
      }

      log.info('Loading not-in-assortment items', { count: diffMakats.length })
      const fetched = await fetchCatalogItemsByMakats(diffMakats)

      const extraMap = new Map()
      for (const [makat, item] of fetched) {
        if (item.status === 'אסור למכירה') continue
        extraMap.set(makat, { ...item, _notInAssortment: true, price: null })
      }

      notInAssortmentItemsByMakat.value = extraMap
      notInAssortmentMakats.value = diffMakats.filter((m) => extraMap.has(m))
      notInAssortmentLoaded.value = true
      log.info('Not-in-assortment items loaded', { count: extraMap.size })
    } catch (err) {
      log.error('Failed to load not-in-assortment items', err)
    } finally {
      notInAssortmentLoading.value = false
    }
  }

  /**
   * Load departments (one-time, cached)
   */
  async function loadDepartments() {
    if (departments.value.length > 0) {
      log.debug('Departments already loaded, skipping')
      return
    }

    departmentsLoading.value = true
    departmentsError.value = null

    try {
      const db = getFirebaseDb()
      const deptRef = collection(db, 'Departments')
      const q = query(deptRef, orderBy('order', 'asc'))
      const snapshot = await getDocs(q)

      departments.value = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))

      log.info('Departments loaded', { count: departments.value.length })
    } catch (err) {
      log.error('Failed to load departments', err)
      departmentsError.value = 'Failed to load departments'
      throw err
    } finally {
      departmentsLoading.value = false
    }
  }

  /**
   * Load subdepartments for a department (cached)
   */
  async function loadSubsForDepartment(deptId) {
    if (!deptId) {
      return
    }

    // Check cache first
    if (subsByDepartmentId.value.has(deptId)) {
      log.debug('Subs already cached for department', { deptId })
      return
    }

    subsLoading.value = true

    try {
      const db = getFirebaseDb()
      const subsRef = collection(db, 'Departments', deptId, 'Subs')
      const q = query(subsRef, orderBy('order', 'asc'))
      const snapshot = await getDocs(q)

      const subs = snapshot.docs.map((doc) => ({
        id: doc.id,
        parentDepartmentId: deptId,
        ...doc.data(),
      }))

      // Update the Map (need to create new Map for reactivity)
      const newMap = new Map(subsByDepartmentId.value)
      newMap.set(deptId, subs)
      subsByDepartmentId.value = newMap

      log.info('Subs loaded for department', { deptId, count: subs.length })
    } catch (err) {
      log.error('Failed to load subs', { deptId, error: err })
    } finally {
      subsLoading.value = false
    }
  }

  /**
   * Load all subdepartments (for search feature)
   */
  let _loadAllSubsPromise = null
  async function loadAllSubs() {
    if (allSubsLoaded.value && departments.value.length > 0) {
      log.debug('All subs already loaded')
      return
    }

    // Deduplicate concurrent calls — return the in-flight promise
    if (_loadAllSubsPromise) return _loadAllSubsPromise

    log.info('Loading all subdepartments for search')

    _loadAllSubsPromise = _doLoadAllSubs()
    try {
      await _loadAllSubsPromise
    } finally {
      _loadAllSubsPromise = null
    }
  }

  async function _doLoadAllSubs() {
    try {
      const db = getFirebaseDb()
      const newMap = new Map(subsByDepartmentId.value)

      for (const dept of departments.value) {
        // Skip if already cached
        if (newMap.has(dept.id)) continue

        const subsRef = collection(db, 'Departments', dept.id, 'Subs')
        const q = query(subsRef, orderBy('order', 'asc'))
        const snapshot = await getDocs(q)

        const subs = snapshot.docs.map((doc) => ({
          id: doc.id,
          parentDepartmentId: dept.id,
          parentDepartmentName: dept.name,
          ...doc.data(),
        }))

        newMap.set(dept.id, subs)
      }

      subsByDepartmentId.value = newMap
      allSubsLoaded.value = departments.value.length > 0
      log.info('All subdepartments loaded')
    } catch (err) {
      log.error('Failed to load all subs', err)
    }
  }

  /**
   * Select a department
   */
  function selectDepartment(deptId, deptName) {
    clearSearch()
    selectedDepartmentId.value = deptId
    selectedDepartmentName.value = deptName
    selectedSubDepartmentId.value = null
    selectedSubDepartmentName.value = null

    // Skip subs loading for virtual promo department (it has no subs)
    if (deptId !== PROMO_DEPARTMENT_ID) {
      loadSubsForDepartment(deptId)
    }
  }

  /**
   * Select a subdepartment
   */
  function selectSubDepartment(subId, subName, deptId, deptName) {
    clearSearch()

    // Update department if different
    if (deptId && deptId !== selectedDepartmentId.value) {
      selectedDepartmentId.value = deptId
      selectedDepartmentName.value = deptName
      loadSubsForDepartment(deptId)
    }

    selectedSubDepartmentId.value = subId
    selectedSubDepartmentName.value = subName
  }

  /**
   * Set search query
   */
  function setSearchQuery(query) {
    searchQuery.value = query
    if (query.trim() && !searchTouched.value) {
      searchTouched.value = true
    }
    // Load all subs on first search for department/sub search
    if (query.trim() && !allSubsLoaded.value) {
      loadAllSubs()
    }
  }

  /**
   * Clear search
   */
  function clearSearch() {
    searchQuery.value = ''
    searchTouched.value = false
  }

  /**
   * Set a catalog filter value
   */
  function setFilter(key, value) {
    if (key === 'showOutOfStock') {
      filterShowOutOfStock.value = value
    } else if (key === 'kosher') {
      filterKosher.value = value
      // Auto-disable rabanut when kosher is turned off
      if (!value) {
        filterRabanut.value = false
      }
    } else if (key === 'rabanut') {
      filterRabanut.value = value
    } else if (key === 'showNotInAssortment') {
      filterShowNotInAssortment.value = value
      if (value) loadNotInAssortmentItems()
    }
  }

  /**
   * Clear client missing popup message
   */
  function clearClientMissingMessage() {
    clientMissingMessage.value = null
  }

  /**
   * Clear agent override state
   * Called when leaving AgentCatalogView to allow normal catalog to re-bootstrap
   */
  function clearAgentOverride() {
    if (agentOverrideClient.value || catalogMode.value === 'agent') {
      log.info('Clearing agent override state')
      agentOverrideClient.value = null
      linkedClient.value = null
      usePromotionsStore().setLinkedClient(null)
      catalogMode.value = 'default'
      selectedDepartmentId.value = null
      selectedDepartmentName.value = null
      isBootstrapped.value = false // Allow re-bootstrap
    }
  }

  /**
   * Reset only filter/selection state (not catalog data).
   * Used when switching between clients in agent catalog.
   */
  function resetFilters() {
    selectedDepartmentId.value = null
    selectedDepartmentName.value = null
    selectedSubDepartmentId.value = null
    selectedSubDepartmentName.value = null
    searchQuery.value = ''
    searchTouched.value = false
    filterShowOutOfStock.value = false
    filterKosher.value = false
    filterRabanut.value = false
    filterShowNotInAssortment.value = false
  }

  // ============================================
  // View Preferences Actions
  // ============================================
  function setItemsPerRow(val) {
    isAutoMode.value = false
    itemsPerRow.value = val
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, String(val))
      localStorage.setItem(AUTO_MODE_STORAGE_KEY, 'false')
    } catch (_e) {}
  }

  function setAutoItemsPerRow() {
    isAutoMode.value = true
    try {
      localStorage.setItem(AUTO_MODE_STORAGE_KEY, 'true')
    } catch (_e) {}
  }

  function toggleDisplayMode() {
    displayMode.value = displayMode.value === 'cards' ? 'grid' : 'cards'
    try {
      localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, displayMode.value)
    } catch (_e) {}
  }

  function setDisplayMode(mode) {
    if (mode === 'grid' || mode === 'cards') {
      displayMode.value = mode
      try {
        localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, mode)
      } catch (_e) {}
    }
  }

  function handleWindowResize() {
    windowWidth.value = window.innerWidth
  }

  /**
   * Update a single item's image URL in place (used after per-item image refresh).
   * Replaces the Map entry so Vue reactivity picks up the change.
   * @param {string} makat
   * @param {string} newImageUrl
   */
  function updateItemImage(makat, newImageUrl) {
    const existing = itemsByMakat.value.get(makat)
    if (!existing) return
    const updated = { ...existing, image: newImageUrl }
    const nextMap = new Map(itemsByMakat.value)
    nextMap.set(makat, updated)
    itemsByMakat.value = nextMap
  }

  /**
   * Reset store to initial state
   */
  function reset() {
    isBootstrapped.value = false
    isBootstrapping.value = false
    bootstrapError.value = null
    catalogLoading.value = false
    itemsByMakat.value = new Map()
    pricesByMakat.value = new Map()
    assortmentMakats.value = []
    assortmentId.value = null
    priceListId.value = null
    catalogMode.value = 'default'
    linkedClient.value = null
    agentOverrideClient.value = null
    usePromotionsStore().setLinkedClient(null)
    clientMissingMessage.value = null
    departments.value = []
    departmentsLoading.value = false
    departmentsError.value = null
    subsByDepartmentId.value = new Map()
    allSubsLoaded.value = false
    subsLoading.value = false
    selectedDepartmentId.value = null
    selectedDepartmentName.value = null
    selectedSubDepartmentId.value = null
    selectedSubDepartmentName.value = null
    searchQuery.value = ''
    searchTouched.value = false
    filterShowNotInAssortment.value = false
    notInAssortmentItemsByMakat.value = new Map()
    notInAssortmentMakats.value = []
    notInAssortmentLoading.value = false
    notInAssortmentLoaded.value = false
    itemsPerRow.value = loadItemsPerRowFromStorage()
    displayMode.value = loadDisplayModeFromStorage()
    windowWidth.value = typeof window !== 'undefined' ? window.innerWidth : 1024
    log.info('Catalog store reset')
  }

  return {
    // Constants
    PROMO_DEPARTMENT_ID,
    // Bootstrap State
    isBootstrapped,
    isBootstrapping,
    bootstrapError,
    catalogLoading,
    // Catalog Data
    itemsByMakat,
    pricesByMakat,
    assortmentMakats,
    assortmentId,
    priceListId,
    catalogMode,
    linkedClient,
    agentOverrideClient,
    clientMissingMessage,
    // Departments
    departments,
    departmentsWithPromo,
    visibleDepartments,
    visibleCurrentDeptSubs,
    visibleSubsByDeptIdObject,
    getVisibleSubsForDept,
    departmentsLoading,
    departmentsError,
    subsByDepartmentId,
    subsByDeptIdObject,
    allSubsLoaded,
    subsLoading,
    // Selection
    selectedDepartmentId,
    selectedDepartmentName,
    selectedSubDepartmentId,
    selectedSubDepartmentName,
    // Search
    searchQuery,
    searchTouched,
    // Filters
    filterShowOutOfStock,
    filterKosher,
    filterRabanut,
    filterShowNotInAssortment,
    notInAssortmentLoading,
    notInAssortmentLoaded,
    notInAssortmentItemsByMakat,
    canShowNotInAssortmentFilter,
    activeFilterCount,
    // Getters
    itemsArray,
    allDisplayableItems,
    applyItemFilters,
    promoItems,
    singlePromoItems,
    multiItemPromotions,
    firstOrderMultiPromos,
    regularMultiPromos,
    firstOrderSinglePromoItems,
    regularSinglePromoItems,
    selectedDepartment,
    currentDeptSubs,
    isSearchActive,
    searchResults,
    filteredItems,
    visibleItems,
    showSearchHelper,
    // View Preferences
    DESKTOP_VIEW_OPTIONS,
    TABLET_VIEW_OPTIONS,
    MOBILE_VIEW_OPTIONS,
    itemsPerRow,
    displayMode,
    isAutoMode,
    autoItemsPerRow,
    windowWidth,
    isMobileView,
    isTabletView,
    isPCView,
    currentViewOptions,
    validItemsPerRow,
    isGridMode,
    isVisitorMode,
    gridStyle,
    setItemsPerRow,
    setAutoItemsPerRow,
    toggleDisplayMode,
    setDisplayMode,
    handleWindowResize,
    // Actions
    bootstrap,
    loadDepartments,
    loadSubsForDepartment,
    loadAllSubs,
    selectDepartment,
    selectSubDepartment,
    setSearchQuery,
    clearSearch,
    setFilter,
    clearClientMissingMessage,
    clearAgentOverride,
    resetFilters,
    updateItemImage,
    reset,
  }
})
